import React from 'react';
import {
  ExportLicenseType,
  ExportAgency,
  ExportLicenseStatus,
} from 'api/ExportHubAPI';

export const LICENSE_TYPES: ExportLicenseType[] = [
  'DSP-5',
  'DSP-73',
  'DSP-61',
  'DSP-85',
  'TAA',
  'MLA',
  'WDA',
  'Other',
];
export const AGENCIES: ExportAgency[] = [
  'State / DDTC',
  'Commerce / BIS',
  'Other',
];
export const LICENSE_STATUSES: ExportLicenseStatus[] = [
  'Requested',
  'Active',
  'Expiring',
  'Expired',
  'Denied',
  'Closed',
];

// Agreement license types expose agreement/party fields (spec §6.1).
export const AGREEMENT_TYPES = new Set<ExportLicenseType>(['TAA', 'MLA', 'WDA']);

// One-line descriptions shown beside each license type in the add-license modal.
export const LICENSE_TYPE_DESCRIPTIONS: Record<ExportLicenseType, string> = {
  'DSP-5': 'Permanent export of unclassified defense articles',
  'DSP-73': 'Temporary export of unclassified defense articles',
  'DSP-61': 'Temporary import of unclassified defense articles',
  'DSP-85': 'Export of classified defense articles',
  TAA: 'Technical Assistance Agreement',
  MLA: 'Manufacturing License Agreement',
  WDA: 'Warehouse Distribution Agreement',
  Other: 'Other authorization type',
};

// Routing / party dropdown options for the add-license modal (spec §6).
//
// These are intentionally EMPTY seed lists. The fields render with a creatable
// select that persists each team's own values (ExportFieldOption via
// CreatableSelect), so customers build their own Business Unit / Segment / CW
// Role vocabularies rather than being shown a pre-seeded (aerospace/defense)
// list that may not match their business. Add back starter values here only if
// a universal default is wanted.
export const BUSINESS_UNITS: string[] = [];

export const BUSINESS_SEGMENTS: string[] = [];

export const CW_ROLES: string[] = [];

export const COUNTRIES = [
  'Australia', 'Austria', 'Belgium', 'Brazil', 'Canada', 'Czechia', 'Denmark',
  'Finland', 'France', 'Germany', 'Greece', 'India', 'Ireland', 'Israel',
  'Italy', 'Japan', 'Mexico', 'Netherlands', 'New Zealand', 'Norway', 'Poland',
  'Portugal', 'Qatar', 'Saudi Arabia', 'Singapore', 'South Korea', 'Spain',
  'Sweden', 'Switzerland', 'Taiwan', 'Türkiye', 'United Arab Emirates',
  'United Kingdom', 'United States',
];

const STATUS_VARIANT: Record<ExportLicenseStatus, string> = {
  Requested: 'info',
  Active: 'success',
  Expiring: 'warning',
  Expired: 'danger',
  Denied: 'danger',
  Closed: 'muted',
};

export const LicenseStatusPill: React.FC<{ value: ExportLicenseStatus }> = ({
  value,
}) => (
  <span className={`eh-pill eh-pill-${STATUS_VARIANT[value] || 'muted'}`}>
    {value}
  </span>
);

export const formatCurrency = (n: number | null | undefined): string =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(n);

// Compact currency for KPI tiles / dense charts (e.g. $4.2M, $850K).
export const formatCompactCurrency = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

// Default issuing authority (mirrors the server, overridable in the wizard).
export const deriveAgency = (
  type: ExportLicenseType,
  eccns: string[],
): ExportAgency => {
  if (type.startsWith('DSP') || AGREEMENT_TYPES.has(type)) return 'State / DDTC';
  if (eccns.length) return 'Commerce / BIS';
  return 'Other';
};
